import { AttributesMixin, define, Store, type } from "@type-r/models";
import { AssetType } from "../../api/assetType";
import { Company, getCompanies } from "../../api/company";
import { getAuthorizationBearer, getPlatformCode } from "../../api/endpoint";
import { ImportTemplateCard, ImportTemplates } from "../../api/importTemplateCard";
import { StatusType } from "../../api/lookups";
import { GetFilteredImportTemplatesRequest, SortingImportTemplate } from "../models/get-filtered-import-templates-request";
import { ImportTemplatesFilters } from "../models/import-templates-filters";

const initSortOptions = {
    attr: 'createdOn',
    asc: false
}

export interface ImportTemplatesState extends AttributesMixin<typeof ImportTemplatesState> { }

@define
export class ImportTemplatesState extends Store {
    static attributes = {
        companies: type([Company]),
        importTemplateCards: type([ImportTemplateCard]),
        filter: type(ImportTemplatesFilters),
        isDefault: type(Boolean),
        isStandard: type(Boolean),
        sort: type(initSortOptions)
    }

    async initialize() {
        this.filter.company.set({
            placeholder: 'Company',
            selected: [],
            getItems: getCompanies
          })

        this.filter.assetType.set({
            selected: 0,
            options: [
                { value: 0, label: "All Asset Types" },
                { value: AssetType.invoice, label: "Invoice" },
                { value: AssetType.insurancePolicy, label: "Insurance Policy" },
                { value: AssetType.payment, label: "Payment" },
                { value: AssetType.remittanceAdvice, label: "Remittance Advice" },
                { value: AssetType.creditNote, label: "Credit Note" },
                { value: AssetType.multiAsset, label: "Multiple Assets" },
                { value: AssetType.unappliedCash, label: "Unapplied Cash" },
                { value: AssetType.customerReferenceData, label: "Customer Reference Data" }
            ] as any
        })
        this.filter.status.set({
            selected: StatusType.All,
            options: [
                { value: StatusType.All, label: 'All Status Types' },
                { value: StatusType.Active, label: 'Active' },
                { value: StatusType.InActive, label: 'Inactive' }
            ]
        })
    }

    mapToQuery = (skip: number = 0, take: number = 30) => {
        return {
            quickSearch: this.filter.filterAll,
            companyIds: Array.from(new Set(this.filter.company.selected as any)),
            assetTypeId: this.filter.assetType.selected as any,
            isActive: this.filter.status.selected === StatusType.Active ?
                true : this.filter.status.selected === StatusType.InActive ? false : null as any,
            sortBy: SortingImportTemplate[this.sort.attr] as any,
            isDefault: this.isDefault,
            isStandard: this.isStandard,
            asc: this.sort.asc,
            skip,
            take
        } as GetFilteredImportTemplatesRequest;
    }

    getFiltered = async (request?: GetFilteredImportTemplatesRequest): Promise<ImportTemplates> => {
        let url: string = 'api/v1/ingest/import-templates';

        if (request) {
            const searchParams = new URLSearchParams();
            if (request.companyIds && request.companyIds.length > 0) {
                request.companyIds.map((id: any) => {
                  searchParams.append("companyIds", `${id}`);
                });
                searchParams.append("filterType", `1`);
            }
            if (request.assetTypeId) {
                searchParams.append("assetTypeId", `${request.assetTypeId}`);
                searchParams.append("filterType", `2`);
            }
            if (request.quickSearch) {
                searchParams.append("quickSearch", `${request.quickSearch}`);
                searchParams.append("filterType", `0`);
            }
            if (request.isActive !== null) {
                searchParams.append("isActive", `${request.isActive}`);
                searchParams.append("filterType", `3`);
            }
            searchParams.append("isDefault", `${request.isDefault}`);
            searchParams.append("isStandard", `${request.isStandard}`);
            searchParams.append("sortBy", `${request.sortBy}`);
            searchParams.append("asc", `${request.asc}`);

            var searchParamsURl = searchParams.toString()
            if (searchParamsURl) {
                url += `?${searchParamsURl}`
            }
        }

        return (await fetch(url, {
            method: 'GET',
            headers: new Headers({
                "Authorization": getAuthorizationBearer(),
                'X-Platform-Code': getPlatformCode()
            }),
        })).json();
    }
}
